import {Component, inject, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ZKeyDetectorService} from '../../services/z-key-detector.service';
import {ModalService} from '../../services/modal.service';
import {LeadGenService} from '../../services/lead-gen.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isScrolled = false;
  mobileMenuOpen = false;
  contactDropdownOpen = false;
  mobileContactDropdownOpen = false;
  apiDropdownOpen = false;
  activeSection = '';
  showToggle = false;
  private readonly email = 'demo@mypart.com';
  private scrollListener?: () => void;

  private modalService = inject(ModalService);
  private leadGenService = inject(LeadGenService);
  readonly isSignedUp = this.leadGenService.signedUp;

  constructor(
    private renderer: Renderer2,
    private zKeyDetector: ZKeyDetectorService
  ) {
  }

  openSignupPopup(): void {
    this.modalService.showLeadGen();
    this.closeMobileMenu();
  }

  ngOnInit(): void {
    // Add handler to prevent body scroll when menu is open
    this.handleBodyScroll();
    // Close dropdowns when clicking outside
    this.setupOutsideClickHandler();
    // Setup scroll detection for active sections
    this.setupActiveSection();
    // Subscribe to z-key detector
    this.setupZKeyDetector();
  }

  ngOnDestroy(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  /**
   * Toggles the mobile navigation menu with animation handling
   */
  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;

    // Toggle body scroll lock
    if (this.mobileMenuOpen) {
      this.renderer.addClass(document.body, 'overflow-hidden');
    } else {
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }

  /**
   * Closes the mobile navigation menu
   */
  closeMobileMenu(): void {
    if (this.mobileMenuOpen) {
      this.mobileMenuOpen = false;
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }

  /**
   * Toggles the desktop contact dropdown
   */
  toggleContactDropdown(): void {
    this.contactDropdownOpen = !this.contactDropdownOpen;
    this.mobileContactDropdownOpen = false; // Close mobile dropdown if open
    this.apiDropdownOpen = false; // Close API dropdown if open
  }

  /**
   * Toggles the API dropdown
   */
  toggleApiDropdown(): void {
    this.apiDropdownOpen = !this.apiDropdownOpen;
    this.contactDropdownOpen = false; // Close contact dropdown if open
    this.mobileContactDropdownOpen = false; // Close mobile contact dropdown if open
  }

  /**
   * Toggles the mobile contact dropdown
   */
  toggleMobileContactDropdown(): void {
    this.mobileContactDropdownOpen = !this.mobileContactDropdownOpen;
    this.contactDropdownOpen = false; // Close desktop dropdown if open
    this.apiDropdownOpen = false; // Close API dropdown if open
  }

  /**
   * Copies email to clipboard
   */
  async copyEmail(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.email);
      // You could add a toast notification here to show success
      console.log('Email copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(this.email);
    }
    this.closeAllDropdowns();
  }

  /**
   * Opens default email app
   */
  openEmailApp(): void {
    window.location.href = `mailto:${this.email}`;
    this.closeAllDropdowns();
  }

  /**
   * Closes all dropdowns
   */
  closeAllDropdowns(): void {
    this.contactDropdownOpen = false;
    this.mobileContactDropdownOpen = false;
    this.apiDropdownOpen = false;
  }

  /**
   * Check if a navigation item is active
   */
  isActive(section: string): boolean {
    return this.activeSection === section;
  }

  /**
   * Toggle between Normal and Gaming mode
   */
  toggleGamingMode(): void {
    this.zKeyDetector.toggleGamingMode();
  }

  /**
   * Get current gaming mode state
   */
  get isGamingMode(): boolean {
    return this.zKeyDetector.isGamingMode();
  }

  /**
   * Fallback copy method for older browsers
   */
  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      console.log('Email copied to clipboard (fallback)');
    } catch (err) {
      console.error('Unable to copy email', err);
    }
    document.body.removeChild(textArea);
  }

  /**
   * Handle body scrolling when mobile menu is open/closed
   */
  private handleBodyScroll(): void {
    // Watch for changes to mobileMenuOpen
    this.watchMobileMenuState();
  }

  /**
   * Watch for changes to the mobile menu state
   */
  private watchMobileMenuState(): void {
    // This method can be expanded in the future to use
    // Angular's change detection if needed
  }

  /**
   * Setup click handler to close dropdowns when clicking outside
   */
  private setupOutsideClickHandler(): void {
    this.renderer.listen('document', 'click', (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.contact-dropdown-container') && !target.closest('.api-dropdown-container')) {
        this.closeAllDropdowns();
      }
    });
  }

  /**
   * Setup active section detection based on scroll position
   */
  private setupActiveSection(): void {
    this.scrollListener = () => {
      this.detectActiveSection();
    };
    window.addEventListener('scroll', this.scrollListener, {passive: true});
    // Set initial active section
    this.detectActiveSection();
  }

  /**
   * Detect which section is currently in view
   */
  private detectActiveSection(): void {
    const sections = ['demo', 'how-it-works', 'pricing', 'why-us', 'song-hub'];
    const offset = 100; // Offset to trigger section change earlier

    for (const sectionId of sections) {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        // Check if section is in view (top of section is within viewport or section contains viewport top)
        if (rect.top <= offset && rect.bottom > offset) {
          this.activeSection = sectionId;
          return;
        }
      }
    }

    // If no section is detected (user is in hero section), don't highlight anything
    this.activeSection = '';
  }

  /**
   * Setup z-key detector subscription
   */
  private setupZKeyDetector(): void {
    this.zKeyDetector.toggleVisible$.subscribe(visible => {
      this.showToggle = visible;
    });
  }
}
